import * as yup from 'yup';
import {
    isValidBrandColor,
    isValidContactEmail,
    isValidDefaultLanguage,
    isValidSocialUrl,
    isValidWhatsAppNumber,
} from '../../../library/models/tenantBranding';
import {
    integerIn,
    predicateRule,
    TranslateFn,
    WATERMARK_OPACITY_BOUNDS,
    WATERMARK_SCALE_BOUNDS,
} from '../../../library/validationRules';
import ResourcesIds from '../../../resources/ResourcesIds';

/**
 * The four predicates in `models/tenantBranding`, wrapped as Yup rules rather than
 * restated: they are the API's own rules ported once and unit tested, and their grain is
 * already the field.
 *
 * The two watermark settings are numbers instead, so they take `integerIn` over the bounds
 * the sliders themselves are built from -- one declaration feeding both, which is what stops
 * a control offering a value the schema then refuses.
 *
 * Empty is valid throughout -- clearing a field is how a clinic takes a link down, and a
 * blank colour is how it falls back to the platform's palette.
 *
 * The contrast warning is deliberately **not** here. It warns below 4.5:1 and does not
 * block: a pale brand is still the clinic's brand, and Motion Care's own yellow is 1.61:1.
 */
export const createBrandingSchema = (translate: TranslateFn) => yup.object({
    primaryColor: predicateRule('hex', isValidBrandColor,
        translate(ResourcesIds.BRANDING_COLOUR_INVALID)),
    secondaryColor: predicateRule('hex', isValidBrandColor,
        translate(ResourcesIds.BRANDING_COLOUR_INVALID)),
    instagramUrl: predicateRule('url', isValidSocialUrl,
        translate(ResourcesIds.BRANDING_SOCIAL_URL_INVALID)),
    facebookUrl: predicateRule('url', isValidSocialUrl,
        translate(ResourcesIds.BRANDING_SOCIAL_URL_INVALID)),
    mapsUrl: predicateRule('url', isValidSocialUrl,
        translate(ResourcesIds.BRANDING_SOCIAL_URL_INVALID)),
    whatsappNumber: predicateRule('phone', isValidWhatsAppNumber,
        translate(ResourcesIds.BRANDING_SOCIAL_WHATSAPP_INVALID)),
    contactEmail: predicateRule('email', isValidContactEmail,
        translate(ResourcesIds.BRANDING_CONTACT_EMAIL_INVALID)),
    defaultLanguage: predicateRule('language', isValidDefaultLanguage,
        translate(ResourcesIds.BRANDING_DEFAULT_LANGUAGE_INVALID)),
    watermarkOpacity: integerIn(WATERMARK_OPACITY_BOUNDS, translate,
        ResourcesIds.BRANDING_WATERMARK_OPACITY_INVALID, { required: true }),
    watermarkScale: integerIn(WATERMARK_SCALE_BOUNDS, translate,
        ResourcesIds.BRANDING_WATERMARK_SCALE_INVALID, { required: true }),
});
